import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { User } from "@supabase/supabase-js";
import { KeyRound, Loader2, Copy, Check } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { API_OPEN } from "@/content/dataPlans";

// The reader's Data API keys on the account page (step 3, 29 Oct 2026). Up to two; a new key is shown once,
// here, and never again (the worker keeps only its fingerprint, in Cloudflare: /api/data/keys in
// cloudflare/worker.js). Until API_OPEN is on, only admins see this card, for the live check.
// app: the assistant a key was made for on the connect page (/data/connect); those sit apart from the two keys
type Key = { id: string; prefix: string; created_at: string; app?: string | null };
type Usage = { tier: "free" | "individual" | "professional"; today: number; per_day: number | null; per_minute: number };
type Answer = Usage & { keys?: Key[]; ok?: boolean; key?: string; prefix?: string; error?: string };
const day = (iso: string) => new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
const n0 = (n: number) => n.toLocaleString("en-US");
// the worker's keys address, with the reader's sign-in
const call = async (method: "GET" | "POST" | "DELETE", query = ""): Promise<{ status: number; body: Answer | null }> => {
  const t = (await supabase.auth.getSession()).data.session?.access_token;
  if (!t) return { status: 401, body: null };
  const r = await fetch(`/api/data/keys${query}`, { method, headers: { Authorization: `Bearer ${t}` } });
  return { status: r.status, body: await r.json().catch(() => null) };
};

export function ApiKeysCard({ user }: { user: User }) {
  const { isRealAdmin } = useAuth();
  const { toast } = useToast();
  const [keys, setKeys] = useState<Key[] | null>(null);
  const [usage, setUsage] = useState<Usage | null>(null);
  const [fresh, setFresh] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const show = API_OPEN || isRealAdmin;

  const load = useCallback(async () => {
    try {
      const { status, body } = await call("GET");
      setKeys(status === 200 && body?.keys ? body.keys : null);
      setUsage(status === 200 && body ? body : null);
    } catch { setKeys(null); setUsage(null); }
  }, []);
  useEffect(() => { if (show) load(); }, [show, load, user.id]);
  if (!show || keys === null) return null;

  const make = async () => {
    setBusy(true);
    const { status, body } = await call("POST").catch(() => ({ status: 0, body: null }));
    setBusy(false);
    if (status !== 200 || !body?.key) {
      toast({ title: "Could not make a key", description: status === 409 ? "You already have two keys. Delete one first." : "Please try again.", variant: "destructive" });
      return;
    }
    setFresh(body.key); setCopied(false);
    load();
  };
  const remove = async (k: Key) => {
    if (!confirm(k.app ? `Disconnect ${k.app}? It stops reading SN Data within a minute.` : `Delete the key ${k.prefix}…? Anything using it stops working within a minute.`)) return;
    setBusy(true);
    const { status } = await call("DELETE", `?id=${encodeURIComponent(k.id)}`).catch(() => ({ status: 0 }));
    setBusy(false);
    if (status !== 200) { toast({ title: "Could not delete the key", description: "Please try again.", variant: "destructive" }); return; }
    if (fresh && fresh.startsWith(k.prefix)) setFresh(null);
    load();
  };
  const copy = () => { navigator.clipboard?.writeText(fresh || "").then(() => setCopied(true), () => {}); };

  const own = keys.filter((k) => !k.app), apps = keys.filter((k) => k.app);
  const plan = usage?.tier === "professional" ? "Professional" : usage?.tier === "individual" ? "Individual" : "Free";
  const limits = usage ? (usage.per_day ? `${n0(usage.today)} of ${n0(usage.per_day)} calls used today (UTC)` : `${n0(usage.today)} calls today (UTC) · up to ${n0(usage.per_minute)} a minute`) : null;

  return (
    <Card className="rounded-none border-0 border-t border-[var(--sn-border)] bg-transparent px-0 py-6 shadow-none">
      <div className="mb-4 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-primary/40 bg-primary/10">
          <KeyRound className="h-5 w-5 text-primary" />
        </div>
        <div className="flex-1">
          <h4 className="font-medium text-foreground">API keys{usage ? ` · ${plan}` : ""}</h4>
          <p className="text-xs text-muted-foreground">{limits || "For the Shikshan Nivesh Data API"}</p>
        </div>
      </div>

      {fresh && (
        <div className="mb-4 rounded-md border border-primary/40 bg-primary/5 p-3">
          <p className="mb-2 text-xs text-foreground">Your new key. Copy it now: it is shown only this once.</p>
          <div className="flex items-center gap-2">
            <code className="min-w-0 flex-1 overflow-x-auto whitespace-nowrap rounded bg-background px-2 py-1.5 font-mono text-[12px] text-foreground">{fresh}</code>
            <Button variant="outline" size="sm" onClick={copy}>{copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}</Button>
          </div>
        </div>
      )}

      {own.length > 0 ? (
        <ul className="mb-4 divide-y divide-[var(--sn-border)] border-y border-[var(--sn-border)] text-sm">
          {own.map((k) => (
            <li key={k.id} className="flex items-center justify-between gap-3 py-2.5">
              <span className="font-mono text-[12.5px] text-foreground">{k.prefix}…</span>
              <span className="flex-1 text-xs text-muted-foreground">made {day(k.created_at)}</span>
              <Button variant="ghost" size="sm" onClick={() => remove(k)} disabled={busy} className="text-muted-foreground">Delete</Button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mb-4 text-sm text-muted-foreground">No keys yet. A key lets your own code, a spreadsheet or an AI assistant read the data.</p>
      )}

      {apps.length > 0 && (
        <>
          <p className="mb-1.5 text-xs text-muted-foreground">Connected assistants</p>
          <ul className="mb-4 divide-y divide-[var(--sn-border)] border-y border-[var(--sn-border)] text-sm">
            {apps.map((k) => (
              <li key={k.id} className="flex items-center justify-between gap-3 py-2.5">
                <span className="text-foreground">{k.app}</span>
                <span className="flex-1 text-xs text-muted-foreground">connected {day(k.created_at)}</span>
                <Button variant="ghost" size="sm" onClick={() => remove(k)} disabled={busy} className="text-muted-foreground">Disconnect</Button>
              </li>
            ))}
          </ul>
        </>
      )}

      <div className="flex flex-wrap items-center gap-3">
        {own.length < 2 && (
          <Button size="sm" onClick={make} disabled={busy}>
            {busy && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
            Make a key
          </Button>
        )}
        <Link to="/data/developers" className="text-xs text-muted-foreground underline underline-offset-4">How to use the API</Link>
      </div>
    </Card>
  );
}
